import { useCallback, useEffect, useState, useMemo } from 'react';
import { Link, useNavigate, useParams, Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAtom } from 'jotai';
import { fromUnixTime, getUnixTime, startOfDay } from 'date-fns';
import { Tooltip, SkeletonLine, Button, IconButton, InfoBox, DateRangePicker } from '@crazyegginc/hatch';

import { DashHeader } from '/src/components/headers/DashHeader';
import { Breadcrumb, BreadcrumbItem } from '/src/components/Breadcrumb';
import { ActionButton } from '/src/features/goals/components/ActionButton';
import { NotificationsButton } from '/src/features/notifications/components/NotificationsButton';
import { DashboardPage } from '/src/components/Page';
import { RenameModal } from '../components/modals/RenameModal';
import { SelectionProvider } from '/src/contexts/selection';

import {
  usePermissions,
  useAuthContext,
  useSite,
  useNotifications,
  useModal,
  useSelection,
  useQueryParams,
} from '/src/hooks';

import { formatLongDate, toUtcEnd, toUtcStart } from '/src/utils/date';
import { flashElement, removeHash, isSharedResourceSubscriptionError } from '/src/utils';
import { getFeatureName } from '/src/features/_global/utils';
import { NOTIFICATION_RESOURCE_TYPES } from '/src/features/notifications/constants';
import { combinedTriggerAndDisplayData } from '/src/features/goals/utils';
import { goalSelectedDatesAtom } from '/src/features/goals/store';
import { FEATURES, TIMEZONES } from '/src/features/_global/constants';
import { MAXIMUM_SELECTION } from '/src/features/goals/constants';

import { ReactComponent as ArrowIcon } from '@crazyegginc/hatch/dist/images/icon-arrow.svg';
import { ReactComponent as EditIcon } from '@crazyegginc/hatch/dist/images/icon-pencil-filled.svg';
import { ReactComponent as PlusIcon } from '@crazyegginc/hatch/dist/images/icon-plus.svg';

// CONSTANTS
const PER_PAGE = 10;

// QUERIES
import { goalDetailQuery, goalCountsQuery } from '/src/features/goals/queries';

// COMPONENTS
import { SEO } from '/src/components/SEO';
import { SitePickerBar } from '/src/components/site-picker';
import { GoalDetailCard } from '/src/features/goals/components/DetailCard';
import { PaginationControls } from '/src/components/PaginationControls';
import { useResetAtom } from 'jotai/utils';

import { GoalAbTestsTable } from '../components/GoalAbTestsTable';
import { GoalPageConversionsTable } from '../components/GoalPageConversionsTable';

// CONTENT :START
// NOTE: AT THE MOMENT USING SITES LENGTH TO SHOW THE CONTENT
function GoalDetail({ goal = null, isFetching = false, error = null }) {
  const { isSharing } = useAuthContext();
  const { addToSelection, currentSelection } = useSelection();
  const notifications = useNotifications();
  const navigate = useNavigate();
  const { id } = useParams();
  const [page, setPage] = useState(1);
  const permissions = usePermissions();
  const canVisitGoals = permissions.can('navigate', FEATURES.GOALS).allowed;
  const [selection, setSelection] = useAtom(goalSelectedDatesAtom);
  const resetGoalSelectedDates = useResetAtom(goalSelectedDatesAtom);
  const { set: paramSet } = useQueryParams();

  const pickerLowerBoundary = goal?.createdAt ? startOfDay(fromUnixTime(goal.createdAt)) : null;
  const pickerUpperBoundary = goal?.createdAt ? new Date() : null;

  useEffect(() => {
    // reset in case there were any previously selected values.
    // it will still respect URL query params.
    resetGoalSelectedDates();
  }, [resetGoalSelectedDates]);

  const abTests = useMemo(() => goal?.abTests ?? [], [goal?.abTests]);
  const paginatedAbTests = useMemo(() => abTests.slice((page - 1) * PER_PAGE, page * PER_PAGE), [abTests, page]);

  const { data: countsData, isFetching: fetchingCounts } = useQuery({
    ...goalCountsQuery({
      id: Number(id),
      presetDate: selection.special,
      startDate: selection.startDate ? toUtcStart(selection.startDate) : undefined,
      endDate: selection.endDate ? toUtcEnd(selection.endDate) : undefined,
    }),
    networkMode: 'always',
    cacheTime: 0,
    keepPreviousData: false,
    onSuccess(goalCountsData) {
      if (!currentSelection || currentSelection.length === 0) {
        const triggersAndCounts = combinedTriggerAndDisplayData(goal, goalCountsData.goalCounts.triggers);

        const visibleTriggers = triggersAndCounts.filter((t) => {
          // if the trigger is deleted, but has data it is visible
          return !t.deletedAt || Object.keys(t.counts).length > 0;
        });

        const maxSelectable = Math.min(MAXIMUM_SELECTION, visibleTriggers.length);

        setTimeout(() => {
          addToSelection([...visibleTriggers.slice(0, maxSelectable)]);
        }, 100);
      }
    },
  });

  useEffect(() => {
    if (error && error.message.match(/Goal not found/)) {
      notifications.error({ content: `No matching goal found`, timeout: 3000, skipHoneybadger: true });
      navigate(canVisitGoals ? '/goals' : '/not-found', { replace: true });
    }
  }, [error, navigate, notifications, canVisitGoals]);

  const canViewGoal = permissions.can('view', goal).allowed;

  useEffect(() => {
    if (goal && !canViewGoal) {
      notifications.error({
        content: 'You do not have permission to view that goal.',
        timeout: 3000,
        skipHoneybadger: true,
      });
      navigate('/goals', { replace: true });
    }
  }, [canViewGoal, goal, navigate, notifications]);

  const counts = countsData?.goalCounts ?? null;
  const abTestsCount = goal?.abTestsCountTotal ?? 0;

  const showABTestTable = abTestsCount > 0 && abTests.length > 0;

  useEffect(() => {
    if (showABTestTable && window.location.hash === '#ab-tests') {
      setTimeout(() => {
        const el = document.querySelector('#ab-tests > div');
        flashElement(el);
        removeHash();
      }, 1000);
    }
  }, [showABTestTable]);

  const chooseDateRange = useCallback(
    (value) => {
      if (value.special) {
        paramSet('date_range', value.special);
        setSelection({ special: value.special, startDate: null, endDate: null });
      } else if (value.start_date && value.end_date) {
        const newStartDate = getUnixTime(value.start_date);
        const newEndDate = getUnixTime(value.end_date);
        paramSet('date_range', `${newStartDate},${newEndDate}`);
        setSelection({ special: null, startDate: newStartDate, endDate: newEndDate });
      }
    },
    [paramSet, setSelection],
  );

  return (
    <div className="mt-10 flex w-full flex-col px-10">
      <div className="mb-3.75 flex items-end justify-between">
        {fetchingCounts ? (
          <SkeletonLine width="35%" height="10px" className="!rounded-sm" />
        ) : (
          <h2 className="text-sm text-lynch-500 leading-none flex flex-1">
            Goal created: {goal?.createdAt ? formatLongDate(goal.createdAt) : 'N/A'}{' '}
            {goal?.createdBy ? `by ${goal.createdBy?.name ? goal.createdBy.name : goal.createdBy.login}` : null}
          </h2>
        )}

        <div className="flex">
          {!isSharing && (
            <div className=" mr-3.75">
              <NotificationsButton
                resourceType={NOTIFICATION_RESOURCE_TYPES.GOAL}
                resourceId={goal?.id}
                variant="ghost-primary"
              />
            </div>
          )}

          {goal?.createdAt ? (
            <DateRangePicker
              startDate={fromUnixTime(selection.startDate)}
              endDate={fromUnixTime(selection.endDate)}
              lowerBoundary={pickerLowerBoundary}
              upperBoundary={pickerUpperBoundary}
              size="base"
              special={selection.special}
              tooltipOptions={{}}
              setDateRange={chooseDateRange}
              setCommonDateRange={chooseDateRange}
              showCommonRanges={true}
              maxRange={365}
              timezone={TIMEZONES.UTC}
            />
          ) : null}
        </div>
      </div>

      <GoalDetailCard
        goal={goal}
        isFetching={isFetching}
        abTestsCount={abTestsCount}
        counts={counts}
        isFetchingCounts={fetchingCounts}
      />

      <GoalPageConversionsTable goal={goal} />

      {showABTestTable ? (
        <>
          <div className="mb-5 mt-10 flex items-center">
            <h2 className="text-header-3 mr-4">A/B Tests using this Goal</h2>
            <InfoBox
              header="Why don’t A/B Test conversions match Total conversions?"
              body="One visitor may successfully complete this goal on multiple tests, however they will only count for one conversion under the total conversion count."
            />
          </div>
          <GoalAbTestsTable loading={isFetching} abTests={paginatedAbTests} />
          {abTests.length >= PER_PAGE && (
            <div className="mt-4">
              <PaginationControls
                currentPage={page}
                perPage={PER_PAGE}
                total={goal?.abTestsCount}
                setPage={(page) => setPage(page)}
              />
            </div>
          )}
        </>
      ) : null}
    </div>
  );
}

function GoalDetailsPageContent() {
  const { isSharing, sharedResource } = useAuthContext();
  const { id } = useParams();
  const navigate = useNavigate();
  const permissions = usePermissions();
  const canVisitDashboard = permissions.can('navigate', FEATURES.GOALS).allowed;
  const canEditGoals = permissions.can('edit', FEATURES.GOALS).allowed;
  const canCreateGoals = permissions.can('create', FEATURES.GOALS).allowed;
  const modal = useModal();
  const [selection] = useAtom(goalSelectedDatesAtom);

  const { sites, loadingSites, selectSite } = useSite({
    dontSelectSite: true,
    sitesQueryProps: { enabled: !isSharing },
  });

  const { data, isFetching, error, isInitialLoading } = useQuery({
    ...goalDetailQuery({
      id: Number(id),
      presetDate: selection.special,
      startDate: selection.startDate,
      endDate: selection.endDate,
    }),
    keepPreviousData: true,
  });

  useEffect(() => {
    if (data?.goalDetail?.siteId) {
      selectSite(data.goalDetail.siteId);
    }
  }, [data, selectSite]);

  const goal = data?.goalDetail ?? null;

  const pickerSite = isSharing
    ? { name: sharedResource?.resource?.siteName, id: sharedResource?.resource?.siteId }
    : sites?.find((s) => s.id === goal?.siteId);

  if (error && isSharedResourceSubscriptionError(error)) {
    return <Navigate replace to="/not-found" />;
  }

  return (
    <DashboardPage>
      <SEO title={getFeatureName(FEATURES.GOALS)} />
      <DashHeader
        titleComponent={
          <Breadcrumb>
            {!isSharing && (
              <BreadcrumbItem active={false} to="/goals">
                Goals
              </BreadcrumbItem>
            )}
            <BreadcrumbItem active={true}>
              <div className="mr-5 flex items-center space-x-1">
                {goal?.name ? (
                  <>
                    <span className="truncate">{goal.name}</span>
                    {goal.permissions.canEdit ? (
                      <Tooltip placement="bottom" tooltipContent="Rename Goal">
                        <IconButton
                          icon={<EditIcon className="h-4 w-4 fill-current" />}
                          className="h-full shrink-0 text-malibu-500 hover:text-dodger-blue-500"
                          onClick={() => {
                            modal.show(<RenameModal goal={goal} />);
                          }}
                          label="edit Goal name"
                        />
                      </Tooltip>
                    ) : null}
                  </>
                ) : isInitialLoading ? (
                  <SkeletonLine width="300px" height="16px" />
                ) : null}
              </div>
            </BreadcrumbItem>
          </Breadcrumb>
        }
        actionButton={
          <div className="flex flex-shrink-0 items-center space-x-2">
            {canVisitDashboard ? (
              <Button
                component={Link}
                variant="ghost-primary"
                to={{
                  pathname: `/goals`,
                }}
                size="xl"
                className="text-center leading-none"
              >
                <ArrowIcon className="mr-2 h-3 w-3 shrink-0 rotate-180 fill-current" />
                Back to all Goals
              </Button>
            ) : null}
            {canEditGoals ? (
              <ActionButton goal={goal} abTests={goal?.abTests ?? []} redirectOnDelete="/goals" variant="primary" />
            ) : null}
            {canCreateGoals ? (
              <Button
                size="xl"
                onClick={() => {
                  navigate('/goals/new');
                }}
                leftIcon={<PlusIcon className="mr-2 h-2.5 w-2.5 fill-current" />}
              >
                Create New Goal
              </Button>
            ) : null}
          </div>
        }
      />
      <SitePickerBar
        sites={pickerSite ? [pickerSite] : []}
        selectedSite={pickerSite}
        loading={isFetching || (loadingSites && !isSharing)}
        pageDisplayName="Goal Details"
      />

      {goal && <GoalDetail goal={goal} isFetching={isFetching} error={error} />}
    </DashboardPage>
  );
}

// ALL COMPONENTS :START
// eslint-disable-next-line no-restricted-syntax
export default function GoalDetailsPage() {
  return (
    <SelectionProvider>
      <GoalDetailsPageContent />
    </SelectionProvider>
  );
}
